import type { CoverKind } from '../data/portfolio'

/**
 * Wireframe "product shots" used until real screenshots exist. They are inline SVG
 * styled through CSS classes (see styles/work.css), so they follow the theme and Cyber mode.
 */

const site = (): string => {
  const navLinks = [0, 1, 2, 3].map((k) => `<rect class="cv-bar" x="${720 + k * 82}" y="41" width="62" height="10" rx="5"/>`).join('')
  const rooms = [0, 1, 2]
    .map(
      (k) => `<g transform="translate(${48 + k * 372} 498)">
        <rect class="cv-card" width="348" height="186" rx="12"/>
        <rect class="cv-img" x="12" y="12" width="324" height="104" rx="8"/>
        <rect class="cv-strong" x="16" y="134" width="${150 + k * 20}" height="14" rx="4"/>
        <rect class="cv-bar" x="16" y="158" width="210" height="10" rx="5"/>
        <rect class="cv-acc-soft" x="270" y="132" width="62" height="40" rx="8"/>
      </g>`,
    )
    .join('')
  return `
    <rect class="cv-acc" x="48" y="34" width="24" height="24" rx="5"/>
    <rect class="cv-strong" x="82" y="39" width="112" height="14" rx="3"/>
    ${navLinks}
    <rect class="cv-acc" x="1048" y="29" width="104" height="34" rx="17"/>
    <rect class="cv-strong" x="48" y="128" width="440" height="40" rx="6"/>
    <rect class="cv-strong" x="48" y="180" width="350" height="40" rx="6"/>
    <rect class="cv-bar" x="48" y="248" width="410" height="12" rx="6"/>
    <rect class="cv-bar" x="48" y="270" width="340" height="12" rx="6"/>
    <rect class="cv-acc" x="48" y="312" width="156" height="46" rx="23"/>
    <rect class="cv-outline" x="216" y="312" width="132" height="46" rx="23"/>
    <g transform="translate(560 106)">
      <rect class="cv-img" width="592" height="300" rx="12"/>
      <circle class="cv-sun" cx="448" cy="92" r="34"/>
      <path class="cv-mtn" d="M0 232 L118 140 L208 202 L322 108 L452 212 L592 150 V300 H0 Z"/>
      <path class="cv-wave" d="M0 262 q20 -10 40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0 t40 0"/>
    </g>
    <g transform="translate(596 372)">
      <rect class="cv-card cv-float" width="520" height="90" rx="14"/>
      <rect class="cv-field" x="18" y="22" width="134" height="46" rx="9"/>
      <rect class="cv-field" x="162" y="22" width="134" height="46" rx="9"/>
      <rect class="cv-field" x="306" y="22" width="88" height="46" rx="9"/>
      <rect class="cv-acc" x="404" y="22" width="98" height="46" rx="9"/>
    </g>
    ${rooms}`
}

const dashboard = (): string => {
  const nav = Array.from({ length: 7 }, (_, k) => {
    const y = 108 + k * 48
    const active = k === 1 ? `<rect class="cv-acc-soft" x="16" y="${y - 13}" width="198" height="38" rx="9"/>` : ''
    return `${active}<rect class="${k === 1 ? 'cv-acc' : 'cv-bar'}" x="54" y="${y}" width="${96 + (k % 3) * 24}" height="12" rx="6"/>
      <rect class="${k === 1 ? 'cv-acc' : 'cv-bar'}" x="28" y="${y - 2}" width="16" height="16" rx="4"/>`
  }).join('')
  const kpis = [0, 1, 2, 3]
    .map((k) => {
      const x = 270 + k * 226
      const spark =
        k % 2 === 0
          ? `<path class="cv-stroke-acc" d="M${x + 130} ${172} l14 -10 l14 6 l14 -18 l14 8 l14 -14"/>`
          : `<path class="cv-stroke-dim" d="M${x + 130} ${160} l14 8 l14 -4 l14 10 l14 -6 l14 4"/>`
      return `<rect class="cv-card" x="${x}" y="94" width="206" height="114" rx="12"/>
        <rect class="cv-bar" x="${x + 18}" y="114" width="78" height="10" rx="5"/>
        <rect class="cv-strong" x="${x + 18}" y="140" width="${92 + k * 6}" height="26" rx="5"/>${spark}`
    })
    .join('')
  const rows = [0, 1, 2, 3, 4]
    .map((k) => {
      const y = 262 + k * 52
      return `<circle class="${k < 2 ? 'cv-acc' : 'cv-ring'}" cx="880" cy="${y + 6}" r="9"/>
        <rect class="cv-bar" x="902" y="${y}" width="${150 + (k % 2) * 60}" height="12" rx="6"/>`
    })
    .join('')
  const kanban = [0, 1, 2, 3]
    .map((c) => {
      const x = 270 + c * 226
      const cards = [0, 1]
        .map((j) => `<rect class="cv-card" x="${x + 10}" y="${564 + j * 58}" width="190" height="48" rx="8"/>
          <rect class="${(c + j) % 3 === 0 ? 'cv-acc' : 'cv-bar'}" x="${x + 24}" y="${582 + j * 58}" width="${60 + j * 40}" height="10" rx="5"/>`)
        .join('')
      return `<rect class="cv-col" x="${x}" y="552" width="210" height="134" rx="10"/>${cards}`
    })
    .join('')
  return `
    <rect class="cv-side" x="0" y="0" width="230" height="706"/>
    <rect class="cv-acc" x="28" y="34" width="24" height="24" rx="5"/>
    <rect class="cv-strong" x="62" y="39" width="104" height="14" rx="3"/>
    ${nav}
    <rect class="cv-strong" x="270" y="36" width="240" height="20" rx="5"/>
    <rect class="cv-field" x="820" y="28" width="250" height="36" rx="18"/>
    <circle class="cv-acc" cx="1130" cy="46" r="18"/>
    ${kpis}
    <g transform="translate(270 232)">
      <rect class="cv-card" width="560" height="300" rx="12"/>
      <path class="cv-gridline" d="M24 80H536M24 140H536M24 200H536M24 260H536"/>
      <path class="cv-area" d="M24 250 C 90 226, 130 164, 200 172 S 320 92, 380 122 S 480 60, 536 70 L536 276 L24 276 Z"/>
      <path class="cv-stroke-acc" d="M24 250 C 90 226, 130 164, 200 172 S 320 92, 380 122 S 480 60, 536 70"/>
      <circle class="cv-acc" cx="380" cy="122" r="7"/>
      <rect class="cv-strong" x="24" y="26" width="150" height="14" rx="4"/>
    </g>
    <rect class="cv-card" x="850" y="232" width="300" height="300" rx="12"/>
    ${rows}
    ${kanban}`
}

const archive = (): string => {
  const depth = [0, 1, 1, 2, 2, 1, 0, 1, 2, 1, 0, 1]
  const tree = depth
    .map((d, k) => {
      const y = 104 + k * 44
      const x = 30 + d * 24
      return `<rect class="${k === 4 ? 'cv-acc' : 'cv-bar'}" x="${x}" y="${y}" width="14" height="12" rx="3"/>
        <rect class="${k === 4 ? 'cv-acc' : 'cv-bar'}" x="${x + 22}" y="${y + 1}" width="${130 - d * 18}" height="10" rx="5"/>`
    })
    .join('')
  const tiles = Array.from({ length: 10 }, (_, k) => {
    const c = k % 5
    const r = Math.floor(k / 5)
    const x = 290 + c * 178
    const y = 104 + r * 222
    const portrait = (k * 7) % 3 === 0
    const art = portrait
      ? `<circle class="cv-acc-strong" cx="${x + 80}" cy="${y + 64}" r="24"/><path class="cv-acc-strong" d="M${x + 38} ${y + 146} q42 -64 84 0 z"/>`
      : `<path class="cv-mtn" d="M${x + 8} ${y + 146} l40 -46 l28 26 l36 -50 l40 70 z"/>`
    return `<g><rect class="cv-card" x="${x}" y="${y}" width="160" height="204" rx="12"/>
      <rect class="cv-img" x="${x + 8}" y="${y + 8}" width="144" height="138" rx="8"/>${art}
      <rect class="cv-strong" x="${x + 12}" y="${y + 160}" width="${84 + (k % 3) * 16}" height="11" rx="4"/>
      <rect class="cv-bar" x="${x + 12}" y="${y + 180}" width="60" height="9" rx="4"/></g>`
  }).join('')
  const ticks = [0, 1, 2, 3, 4, 5, 6]
    .map((k) => `<circle class="${k === 4 ? 'cv-acc' : 'cv-ring'}" cx="${300 + k * 136}" cy="622" r="${k === 4 ? 9 : 6}"/>
      <rect class="cv-bar" x="${280 + k * 136}" y="646" width="40" height="9" rx="4"/>`)
    .join('')
  return `
    <rect class="cv-side" x="0" y="0" width="250" height="706"/>
    <rect class="cv-acc" x="28" y="34" width="24" height="24" rx="5"/>
    <rect class="cv-strong" x="62" y="39" width="120" height="14" rx="3"/>
    <path class="cv-gridline" d="M38 116V560M62 160V280M86 248V270"/>
    ${tree}
    <rect class="cv-field" x="290" y="28" width="620" height="46" rx="23"/>
    <circle class="cv-ring" cx="320" cy="51" r="9"/>
    <rect class="cv-bar" x="342" y="46" width="200" height="10" rx="5"/>
    <rect class="cv-acc-soft" x="936" y="31" width="96" height="40" rx="20"/>
    <rect class="cv-outline" x="1042" y="31" width="108" height="40" rx="20"/>
    ${tiles}
    <path class="cv-gridline-strong" d="M290 622H1170"/>
    ${ticks}`
}

const BUILDERS: Record<CoverKind, () => string> = { site, dashboard, archive }

/**
 * `uid` must be unique per rendered instance (ids inside inline SVG are document-global).
 * `wide` frames the same composition for ~21:9 slots without cropping the window chrome.
 */
export function coverSvg(kind: CoverKind, uid: string, wide = false): string {
  const clip = `cv-clip-${uid}`
  const glow = `cv-glow-${uid}`
  const grid = `cv-grid-${uid}`
  const viewBox = wide ? '-330 72 2260 968' : '0 0 1600 1000'
  return `<svg class="cv" viewBox="${viewBox}" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="${glow}" gradientUnits="userSpaceOnUse" cx="1300" cy="80" r="1300">
        <stop offset="0" class="cv-glow-stop" stop-opacity="0.24"/>
        <stop offset="1" class="cv-glow-stop" stop-opacity="0"/>
      </radialGradient>
      <pattern id="${grid}" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M40 0H0V40" class="cv-grid"/>
      </pattern>
      <clipPath id="${clip}"><rect x="200" y="140" width="1200" height="760" rx="16"/></clipPath>
    </defs>
    <rect class="cv-bg" x="-800" y="-200" width="3200" height="1400"/>
    <rect x="-800" y="-200" width="3200" height="1400" fill="url(#${grid})"/>
    <rect x="-800" y="-200" width="3200" height="1400" fill="url(#${glow})"/>
    <g class="cv-frame">
      <rect class="cv-shadow" x="230" y="190" width="1140" height="740" rx="24"/>
      <g clip-path="url(#${clip})">
        <rect class="cv-win" x="200" y="140" width="1200" height="760"/>
        <rect class="cv-chrome" x="200" y="140" width="1200" height="54"/>
        <circle class="cv-dot" cx="232" cy="167" r="6"/>
        <circle class="cv-dot" cx="254" cy="167" r="6"/>
        <circle class="cv-dot" cx="276" cy="167" r="6"/>
        <rect class="cv-bar" x="640" y="157" width="320" height="20" rx="10"/>
        <g transform="translate(200 194)">${BUILDERS[kind]()}</g>
      </g>
      <rect class="cv-win-border" x="200" y="140" width="1200" height="760" rx="16"/>
    </g>
  </svg>`
}
